import 'react-image-crop/dist/ReactCrop.css';

import AiProducerIcon from '@src/svgs/AiProducerIcon';
import CircleExclammationIcon from '@src/svgs/CircleExclammationIcon';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import Checkbox from '../common/Checkbox';
import { ConfirmModal } from '../common/ConfirmModal';
import { DialogTitle } from '../common/dialogStyles';
import Modal from '../common/Modal';
import Tabs from '../common/Tabs';
import { MODAL_TOOLTIP_STYLE } from '../common/tooltipStyles';
import ReactCrop from '../react-image-multi-crop';
import { ReframeSpeakersTab } from '../stage/reframe-video-modal/ReframeSpeakersTab';
import { TabPanel, TabPanels } from '../stage/reframe-video-modal/tabPanels';
import { UnmappedSpeakerCheckbox } from '../stage/reframe-video-modal/UnmappedSpeakerCheckbox';
import {
  countScenesFramedDifferently,
  cropsEqual,
  type FitOption,
  framingCautionApplies,
  getSlotColor,
  type SlotColorStyle,
} from '../stage/reframe-video-modal/utils';
import { ClipSceneStrip } from './ClipSceneStrip';
import { useClipReframeModal } from './hooks/useClipReframeModal';

import type { Crop } from '../react-image-multi-crop';
import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { Renderer } from '@src/renderer/Renderer';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';

/**
 * Percent crops round-trip through the DOM as floats, so a crop that was only
 * clicked can come back a hair off. Below this it is the same framing.
 */
const CROP_EPSILON = 0.01;

/**
 * What applying to all costs, said plainly. Shown at every output ratio: the
 * number of scenes it rewrites is worth knowing whatever the clip is shaped
 * like. The Auto-Framing caveat on top of it is not — see `CautionMarker`.
 */
const replacedFramingImpact = (sceneCount: number): string =>
  `Replaces framing on ${sceneCount} ${sceneCount === 1 ? 'scene' : 'scenes'}.`;

/** What the marker adds over the line above, where per-speaker framing lives. */
const AUTO_FRAMING_CAUTION =
  'The new framing overwrites speaker auto-framing for each scene.';

/**
 * The one line under the checkbox. Kept to one line and to the width left
 * beside the checkbox, so the row never grows and the centred modal never
 * resizes — hence the shorter preview phrase once the impact has been said.
 */
type ReframeTab = 'framing' | 'speakers';
const REFRAME_TABS: Array<{ id: ReframeTab; label: string }> = [
  { id: 'framing', label: 'Framing' },
  { id: 'speakers', label: 'Speakers' },
];

const applyAllHint = (
  sceneCount: number,
  showsSceneStrip: boolean,
): string | null => {
  const impact = sceneCount > 0 ? replacedFramingImpact(sceneCount) : null;
  if (!showsSceneStrip) {
    return impact;
  }
  return impact
    ? `${impact} Click a scene above to preview.`
    : 'Click a scene above to preview this framing. Apply to save.';
};

const cropMoved = (
  next: Crop | undefined,
  previous: Crop | undefined,
): boolean => !!next && !!previous && !cropsEqual(next, previous, CROP_EPSILON);

type ClipPreviewReframeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  editorStore: ClipPreviewEditorStore;
  videoSourceStore: VideoSourceStore;
  renderer: Renderer | null;
  isReady: boolean;
  aspectRatio: string;
  initialTime: number;
  /**
   * Show the read-only scenes strip under the crop area, so a creator can see
   * how the clip is divided and reframe another shot without leaving the
   * modal. Gated by the same flag as the clip preview's strip (PLAT-11234).
   */
  showSceneStrip?: boolean;
};

export const ClipPreviewReframeModal: React.FC<
  ClipPreviewReframeModalProps
> = ({
  isOpen,
  onClose,
  editorStore,
  videoSourceStore,
  renderer,
  isReady,
  aspectRatio,
  initialTime,
  showSceneStrip = false,
}) => {
  const [activeTab, setActiveTab] = useState<ReframeTab>('framing');
  const {
    videoSource,
    videoTime,
    isResetDisabled,
    videoRef,
    activeSlot,
    orderedSlots,
    currentFit,
    activeSlotIndex,
    displayCrops,
    displayCrop,
    defaultVideoAspectRatio,
    setDefaults,
    handleCropChange,
    handleMultiCropChange,
    handleClose,
    handleSlotSelect,
    handleApply,
    applyToAllScenes,
    setApplyToAllScenes,
    syncVideoSourceAndTime,
    showAutoFrameZoomView,
    autoFrameDisabledReason,
    handleAutoFrameZoomView,
    scenes,
    sceneElements,
    clipDuration,
    currentTime,
    goToScene,
    speakersTab,
    unmappedSpeaker,
    applyToUnmappedSpeaker,
    setApplyToUnmappedSpeaker,
    unmappedSpeakerOffer,
  } = useClipReframeModal({
    editorStore: editorStore!,
    videoSourceStore: videoSourceStore!,
    renderer,
    isReady,
    aspectRatio,
    initialTime,
    closeModal: () => {
      setActiveTab('framing');
      onClose();
    },
    speakersTabActive: activeTab === 'speakers',
  });

  /**
   * Crop edits live in the modal's local state until Apply, and navigating to
   * another scene re-derives the slots from the timeline — which would drop
   * them. Tracked here rather than in the shared reframe hook so Creator
   * Studio's own reframe modal is untouched.
   */
  const [hasCropEdits, setHasCropEdits] = useState(false);
  /** Scene start awaiting confirmation because crop edits would be lost. */
  const [pendingSceneTime, setPendingSceneTime] = useState<number | null>(null);

  const navigateToScene = useCallback(
    async (time: number) => {
      // While previewing, the crop rides along to the scene being moved to, so
      // it is still an unapplied edit when it gets there. Clearing the flag
      // would let a later navigation — after the box is unticked — drop it
      // without asking.
      if (!applyToAllScenes) {
        setHasCropEdits(false);
      }
      setPendingSceneTime(null);
      await goToScene(time);
    },
    [goToScene, applyToAllScenes],
  );

  const handleSceneSeek = useCallback(
    (time: number) => {
      // Nothing to lose while previewing: the framing is carried to the scene
      // clicked rather than replaced by that scene's own, so the dialog asking
      // whether to save it first has nothing to ask about.
      if (hasCropEdits && !applyToAllScenes) {
        setPendingSceneTime(time);
        return;
      }
      void navigateToScene(time);
    },
    [hasCropEdits, applyToAllScenes, navigateToScene],
  );

  /**
   * Keep the framing edits and carry on to the scene the creator asked for.
   * `keepOpen` so applying doesn't end the session — they clicked a scene to
   * go there, not to leave.
   */
  const applyThenNavigate = useCallback(
    async (time: number) => {
      const applied = await handleApply({ keepOpen: true });
      if (!applied) {
        // The crops are still only in slot state, so navigating would discard
        // the edits this action exists to keep. Stay, and leave the dialog up
        // with its other two ways out.
        return;
      }
      await navigateToScene(time);
    },
    [handleApply, navigateToScene],
  );

  /**
   * Only a crop that actually moved counts as an edit. ReactCrop re-emits the
   * current values on a click that selects a slot without dragging it, and
   * `onChange` fires on selection alone — the reframe hook ignores it outright
   * (`onChangeCrops` is what carries the change), so flagging on either one is
   * how a creator who touched nothing got asked to save.
   */
  const handleMultiCropChangeTracked = useCallback<
    typeof handleMultiCropChange
  >(
    (pixelCrops, percentCrops, changedIndex) => {
      const moved =
        changedIndex === -1
          ? percentCrops.some((crop, index) =>
              cropMoved(crop, displayCrops[index]),
            )
          : cropMoved(percentCrops[changedIndex], displayCrops[changedIndex]);

      if (moved) {
        setHasCropEdits(true);
      }
      handleMultiCropChange(pixelCrops, percentCrops, changedIndex);
    },
    [handleMultiCropChange, displayCrops],
  );

  /**
   * Scenes that carry a framing of their own, which applying to all replaces.
   * Zero on a clip where every scene already shares this framing — there is
   * nothing to lose there, and a warning that cries wolf gets clicked through.
   */
  const scenesFramedDifferently = useMemo(
    () => countScenesFramedDifferently(scenes, orderedSlots),
    [scenes, orderedSlots],
  );

  /**
   * The Auto-Framing caveat, only where per-speaker framing is the norm. The
   * count itself is said at every ratio; this is the extra the marker carries.
   */
  const showsAutoFramingCaution =
    applyToAllScenes &&
    scenesFramedDifferently > 0 &&
    framingCautionApplies(aspectRatio);

  // Per instance: two reframe modals can be mounted at once (see the scenes
  // strip's own `useId`), and a shared id fires both tooltips off one hover.
  const cautionTooltipId = useId();

  const activeSlotColor = getSlotColor(activeSlotIndex);

  const frameStyle = useMemo<SlotColorStyle>(
    () => ({
      '--slot-color': activeSlotColor,
    }),
    [activeSlotColor],
  );

  const cropStyleResolver = useCallback(
    (index: number) => {
      const slotColor = getSlotColor(index);
      const isActive = index === activeSlotIndex;

      return {
        '--slot-color': slotColor,
        boxShadow: isActive
          ? `0 0 0 2px ${slotColor}`
          : '0 0 0 1px rgba(3, 4, 26, 0.5)',
        transition: 'box-shadow var(--transition-normal) ease',
      };
    },
    [activeSlotIndex],
  );

  useEffect(() => {
    if (!isOpen || !isReady) return;
    setDefaults();
  }, [isOpen, isReady]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = videoTime;
    }
  }, [videoTime, videoRef]);

  useEffect(() => {
    syncVideoSourceAndTime();
  }, [syncVideoSourceAndTime]);

  const aspectRatioForContainer = defaultVideoAspectRatio;

  // No video element at this playhead position (intro/outro card, text overlay, etc.)
  // or slots exist but no video source URL could be resolved.
  const hasNoVideoAtPlayhead = orderedSlots.length === 0 || !videoSource;

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleClose}
      // Both modals listen for Escape on `document`, so without this the key
      // dismisses the confirm *and* closes the reframe modal underneath it —
      // discarding the framing the confirm exists to protect.
      disableEscapeClose={pendingSceneTime !== null}
      contentStyle={{
        minWidth: '600px',
        maxWidth: '1600px',
        maxHeight: '1000px',
        overflow: 'hidden',
      }}
    >
      <Wrapper>
        <ModalHeading>Reframing</ModalHeading>
        <SubHeading>Set your framing</SubHeading>
        <TabsRow>
          <Tabs
            tabs={REFRAME_TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        </TabsRow>
        <TabPanels>
          <TabPanel
            $hidden={activeTab !== 'framing'}
            aria-hidden={activeTab !== 'framing'}
          >
            {!hasNoVideoAtPlayhead &&
              (orderedSlots.length > 1 || showAutoFrameZoomView) && (
                <ControlsRow>
                  {orderedSlots.length > 1 && (
                    <SlotSelector role="tablist" aria-label="Layout slots">
                      {orderedSlots.map((slot, index) => {
                        const slotColor = getSlotColor(index);
                        return (
                          <SlotButton
                            key={slot.id}
                            type="button"
                            $active={slot.id === activeSlot?.id}
                            $color={slotColor}
                            onClick={() => handleSlotSelect(slot.id)}
                            role="tab"
                            aria-selected={slot.id === activeSlot?.id}
                          >
                            {slot.slotName || `Slot ${index + 1}`}
                          </SlotButton>
                        );
                      })}
                    </SlotSelector>
                  )}
                  {showAutoFrameZoomView && (
                    <SecondaryButton
                      type="button"
                      onClick={handleAutoFrameZoomView}
                      disabled={!!autoFrameDisabledReason}
                      title={autoFrameDisabledReason ?? ''}
                    >
                      <AiProducerIcon
                        strokeColor="currentColor"
                        width="16"
                        height="16"
                        viewBox="0 0 22 20"
                      />
                      Auto-Frame Zoom View
                    </SecondaryButton>
                  )}
                </ControlsRow>
              )}
            <ModalContent>
              {hasNoVideoAtPlayhead ? (
                <NoVideoSection aspectRatio={aspectRatioForContainer || '16:9'}>
                  <NoVideoMessage>No video in this section</NoVideoMessage>
                  <NoVideoHint>
                    Move the playhead to a section with video to adjust framing
                  </NoVideoHint>
                </NoVideoSection>
              ) : (
                <VideoFrameContainer
                  aspectRatio={aspectRatioForContainer || '16:9'}
                  style={frameStyle}
                >
                  {activeSlotIndex >= 0 && (
                    <ReactCrop
                      crop={displayCrop}
                      crops={displayCrops}
                      activeCropIndex={
                        activeSlotIndex >= 0 ? activeSlotIndex : undefined
                      }
                      onActiveCropChange={index => {
                        const nextSlot = orderedSlots[index];
                        if (nextSlot) {
                          handleSlotSelect(nextSlot.id);
                        }
                      }}
                      onChange={handleCropChange}
                      onChangeCrops={handleMultiCropChangeTracked}
                      getCropStyleForIndex={cropStyleResolver}
                      cropAspects={orderedSlots.map(slot => slot.aspectRatio)}
                      minHeight={100}
                      minWidth={100}
                    >
                      <VideoPreviewWrapper
                        aspectRatio={aspectRatioForContainer || '16:9'}
                      >
                        <VideoPreviewContainer
                          fit={currentFit}
                          src={videoSource}
                          autoPlay={false}
                          controls={false}
                          ref={videoRef}
                        />
                      </VideoPreviewWrapper>
                    </ReactCrop>
                  )}
                </VideoFrameContainer>
              )}
              {/* Under the picture, as in the clip preview. Shown even with no
              video at the playhead — that dead end is exactly where a creator
              needs a way to reach a section that has some. */}
              {showSceneStrip && scenes.length > 0 && (
                <SceneStripRow>
                  <ClipSceneStrip
                    scenes={scenes}
                    elements={sceneElements}
                    duration={clipDuration}
                    currentTime={currentTime}
                    onSeek={handleSceneSeek}
                  />
                </SceneStripRow>
              )}
            </ModalContent>
            {pendingSceneTime !== null && (
              <ConfirmModal
                closeModal={() => setPendingSceneTime(null)}
                onCancel={() => void navigateToScene(pendingSceneTime)}
                onConfirm={() => void applyThenNavigate(pendingSceneTime)}
                title="Unapplied Framing Changes"
                message="You have changed the framing here but not applied it yet. Moving to another scene reloads that scene's framing."
                confirmText="Apply Framing"
                cancelText="Discard & Move"
                // Applying is the affirmative, recommended path, so it takes the
                // solid green confirm slot; discarding the framing is the quiet
                // secondary. Never give the data-losing option the primary fill.
                accentColor="var(--primary)"
              />
            )}
            {!hasNoVideoAtPlayhead && scenes.length > 1 && (
              <ApplyAllRow>
                {unmappedSpeakerOffer && unmappedSpeaker && (
                  <UnmappedSpeakerCheckbox
                    offer={unmappedSpeakerOffer}
                    checked={applyToUnmappedSpeaker}
                    onChange={setApplyToUnmappedSpeaker}
                    onLinkSpeaker={unmappedSpeaker.linkSpeaker}
                  />
                )}
                <Checkbox
                  label="Apply framing to all scenes"
                  checked={applyToAllScenes}
                  color={
                    applyToAllScenes ? 'var(--accent)' : 'var(--background)'
                  }
                  borderColor={
                    applyToAllScenes ? 'var(--accent)' : 'var(--border)'
                  }
                  onClick={() => setApplyToAllScenes(!applyToAllScenes)}
                  overrides={{ containerStyles: { 'align-items': 'center' } }}
                />
                {showsAutoFramingCaution && (
                  <>
                    <CautionMarker
                      data-tooltip-id={cautionTooltipId}
                      data-tooltip-content={AUTO_FRAMING_CAUTION}
                      aria-label={AUTO_FRAMING_CAUTION}
                    >
                      <CircleExclammationIcon
                        width="13"
                        height="13"
                        strokeColor="currentColor"
                        strokeWidth={1.75}
                      />
                    </CautionMarker>
                    <Tooltip
                      id={cautionTooltipId}
                      style={{ ...MODAL_TOOLTIP_STYLE, maxWidth: '280px' }}
                    />
                  </>
                )}
                {/* One line, always: the modal is centred, so a hint that grows
                the row resizes and shifts the whole dialog. The Auto-Framing
                caveat that would have been a second line hangs off the marker
                instead. */}
                <ApplyAllHint>
                  {applyToAllScenes
                    ? applyAllHint(scenesFramedDifferently, showSceneStrip)
                    : null}
                </ApplyAllHint>
              </ApplyAllRow>
            )}
            <Buttons>
              <OutlineButton
                onClick={
                  hasNoVideoAtPlayhead
                    ? handleClose
                    : () => {
                        setHasCropEdits(false);
                        setDefaults();
                      }
                }
                disabled={
                  hasNoVideoAtPlayhead ? false : !videoSource || isResetDisabled
                }
              >
                {hasNoVideoAtPlayhead ? 'Close' : 'Reset'}
              </OutlineButton>
              <PrimaryButton
                // Called with no args: passing `handleApply` straight to onClick
                // would hand it the click event as its options object.
                onClick={
                  hasNoVideoAtPlayhead ? handleClose : () => handleApply()
                }
                disabled={hasNoVideoAtPlayhead ? false : !videoSource}
              >
                {hasNoVideoAtPlayhead ? 'OK' : 'Apply'}
              </PrimaryButton>
            </Buttons>
          </TabPanel>
          <TabPanel
            $hidden={activeTab !== 'speakers'}
            aria-hidden={activeTab !== 'speakers'}
          >
            <ReframeSpeakersTab view={speakersTab} />
            <Buttons>
              <PrimaryButton
                onClick={handleClose}
                // A face assignment is saving: closing now would drop the
                // reframe that follows it.
                disabled={speakersTab.saveStatus === 'saving'}
              >
                {speakersTab.saveStatus === 'saving' ? 'Saving…' : 'Done'}
              </PrimaryButton>
            </Buttons>
          </TabPanel>
        </TabPanels>
      </Wrapper>
    </Modal>
  );
};

const TabsRow = styled.div`
  width: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 48px 24px;
  border-radius: 16px;
  border: 1px solid var(--border);
  box-sizing: border-box;
  background: var(--background);
`;

const NoVideoSection = styled.div<{ aspectRatio: string }>`
  width: 100%;
  height: 350px;
  aspect-ratio: ${props => props.aspectRatio.replace(':', ' / ')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  background: var(--surface-elevated, #1a1a2e);
  border-radius: 8px;
`;

const NoVideoMessage = styled.div`
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: 18px;
  font-weight: var(--font-weight-semibold);
`;

const NoVideoHint = styled.div`
  color: var(--text-tertiary, rgba(255, 255, 255, 0.4));
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  text-align: center;
  max-width: 300px;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 8px;
`;

/**
 * Above the buttons rather than beside them: it changes what Apply does, and
 * a creator who reads the button first has to have passed it.
 */
const ApplyAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  margin-top: -12px;
`;

/**
 * Reserved whether or not there is anything to say, so ticking the box never
 * changes the row's height — and one line only, for the same reason.
 */
const ApplyAllHint = styled.span`
  min-height: var(--leading-text-xs);
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
`;

/**
 * Amber, because it marks what the Apply takes away rather than what it does.
 * A marker rather than a line of copy: the caution is only relevant while the
 * box is ticked, and text that comes and goes resizes a centred modal.
 */
const CautionMarker = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: var(--warning, #b58105);
  cursor: help;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
`;

const SlotSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const SlotButton = styled.button<{ $active: boolean; $color: string }>`
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid
    ${props => (props.$active ? props.$color : 'rgba(72, 72, 72, 0.8)')};
  background: ${props => (props.$active ? props.$color : 'transparent')};
  color: ${props =>
    props.$active ? 'var(--background)' : 'var(--text-secondary)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.6px;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    background var(--transition-normal) ease,
    border-color var(--transition-normal) ease,
    color var(--transition-normal) ease;

  &:hover {
    border-color: ${props => props.$color};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.$color};
    outline-offset: 2px;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: var(--btn-h-md);
  padding: 0 var(--btn-px-md);
  border-radius: var(--radius-button);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-fast);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }

  &:disabled {
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const OutlineButton = styled.button`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  cursor: pointer;

  background: var(--surface);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  line-height: normal;

  border-radius: 8px;
  border: 1px solid var(--border);
  transition: var(--transition-fast);

  &:hover {
    border-color: var(--primary);
  }

  &:disabled {
    color: var(--border);
    border: 1px solid var(--border);
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(OutlineButton)`
  background: var(--primary);
  color: var(--background);
  border: none;
  font-weight: 600;
  width: 100%;

  &:hover {
    background: var(--primary-hover);
    border-color: transparent;
  }
`;

const ModalHeading = styled(DialogTitle)``;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: max-content;
`;

const SceneStripRow = styled.div`
  width: 100%;
  margin-top: 12px;
`;

const SubHeading = styled.p`
  color: var(--foreground);
  text-align: center;
  font-family: var(--font-primary);
  font-size: 32px;
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: 120%;
  margin: 0;
`;

const VideoFrameContainer = styled.div<{ aspectRatio: string }>`
  width: 100%;
  height: 350px;
  aspect-ratio: ${props => props.aspectRatio.replace(':', ' / ')};
  display: flex;
  justify-content: center;
  position: relative;
  overflow: visible;
  background: #000;

  .ReactCrop {
    width: 100%;
    height: 100%;
  }

  @keyframes marching-ants {
    0% {
      background-position:
        0px 0px,
        0px 100%,
        0px 0px,
        100% 0px;
    }
    100% {
      background-position:
        16px 0px,
        -16px 100%,
        0px 16px,
        100% -16px;
    }
  }

  .ReactCrop__crop-selection:not(
    .ReactCrop--no-animate .ReactCrop__crop-selection
  ) {
    background-image:
      linear-gradient(
        to right,
        var(--slot-color, var(--primary)) 0%,
        var(--slot-color, var(--primary)) 40%,
        transparent 40%,
        transparent 100%
      ),
      linear-gradient(
        to right,
        var(--slot-color, var(--primary)) 0%,
        var(--slot-color, var(--primary)) 40%,
        transparent 40%,
        transparent 100%
      ),
      linear-gradient(
        to bottom,
        var(--slot-color, var(--primary)) 0%,
        var(--slot-color, var(--primary)) 40%,
        transparent 40%,
        transparent 100%
      ),
      linear-gradient(
        to bottom,
        var(--slot-color, var(--primary)) 0%,
        var(--slot-color, var(--primary)) 40%,
        transparent 40%,
        transparent 100%
      );
    background-size:
      16px 2px,
      16px 2px,
      2px 16px,
      2px 16px !important;
    background-position:
      0px 0px,
      0px 100%,
      0px 0px,
      100% 0px;
    background-repeat: repeat-x, repeat-x, repeat-y, repeat-y !important;
    animation: marching-ants 1s linear infinite;
  }

  .ReactCrop__drag-handle {
    background-color: var(--slot-color, var(--primary));
    border: none;
    width: 10px;
    height: 10px;
    margin: 2px;
    border-radius: 50%;
  }
`;

const VideoPreviewWrapper = styled.div<{ aspectRatio: string }>`
  height: 350px;
  aspect-ratio: ${props => props.aspectRatio.replace(':', ' / ')};
  position: relative;
  overflow: visible;
`;

const VideoPreviewContainer = styled.video<{ fit: FitOption }>`
  width: 100%;
  height: 100%;
  position: absolute;
  border-radius: 8px;
  top: 0;
  left: 0;
  pointer-events: none;
  object-fit: ${props => props.fit};
`;
